import type {
  ColumnAlignment,
  ColumnFilter,
  ColumnFooterRenderer,
  Comparer,
  Matcher,
  Renderer,
  RowAction,
} from "@/components/controls/grid/BmGridData";
import {
  BmGridColumnConfiguration,
  componentToRenderer,
} from "@/components/controls/grid/BmGridData";
import DataCellActions from "@/components/controls/grid/renderers/DataCellActions.vue";
import type { EditableRendererMetadata } from "@/components/controls/grid/renderers/editors/types";
import type { VueConstructor } from "vue";
import DataCellControl from "@/components/controls/grid/renderers/DataCellControl.vue";
import type { Location } from "vue-router";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import NumberFilterComponent, {
  buildApplyNumberFilter,
} from "@/components/controls/grid/columnfilters/NumberColumnFilter";
import Key from "@/app/types/common/key";
import type { BmEnumType } from "@/app/services/baseEnumType";
import type BaseEnumType from "@/app/services/baseEnumType";
import DataCellTextInfo from "@/components/controls/grid/renderers/info/DataCellTextInfo.vue";
import type { TruncateOptions } from "lodash";
import type { ComponentPublicInstanceConstructor } from "vue/types/v3-component-public-instance";
import messages, { translate } from "@/plugins/i18n";

function isVueComponent(
  renderer: VueConstructor<Vue> | Renderer
): renderer is VueConstructor<Vue> {
  return "component" in renderer;
}

export interface ColumnBuilderDefaultState {
  hidden?: boolean;
  groupByIndex?: number;
  sortByIndex?: number;
  sortByDescending?: boolean;
  filterState?: {};
}

export interface TextTruncationOptions<T> extends TruncateOptions {
  title: string;
  props: any;
  width: number;
  extractor: (item: T) => any;
}

export type ColumnKey = Key | { id: string; headingText: string };

export class ColumnBuilder<T, V> {
  protected readonly _gridConfiguration: BmGridColumnConfiguration;
  protected readonly _defaultState: ColumnBuilderDefaultState = {};
  private readonly _columnKey: string;

  constructor(columnKey: ColumnKey, itemValue: (item: T) => any) {
    this._columnKey = columnKey instanceof Key ? columnKey.value : columnKey.id;

    this._gridConfiguration = new BmGridColumnConfiguration(
      columnKey instanceof Key ? translate(columnKey) : columnKey.headingText,
      itemValue,
      {
        groupable: true,
        sortableAndFilterable: true,
      }
    );
  }

  public build(): [string, BmGridColumnConfiguration] {
    return [this._columnKey, this._gridConfiguration];
  }

  public getDefaultState(): [string, ColumnBuilderDefaultState] {
    return [this._columnKey, this._defaultState];
  }

  public withHeadingText(headingText: string) {
    this._gridConfiguration.headingText = headingText;
    return this;
  }

  public withAlignment(alignment: ColumnAlignment) {
    this._gridConfiguration.align = alignment;
    return this;
  }

  public withCardColumnSpan(cardColumnSpan: number) {
    this._gridConfiguration.cardColumnSpan = cardColumnSpan;
    return this;
  }

  public withRenderer(renderer: Renderer) {
    this._gridConfiguration.renderer = renderer;
    return this;
  }

  public withComparer(comparer: Comparer) {
    this._gridConfiguration.comparer = comparer;
    return this;
  }

  public withMatcher(matcher: Matcher) {
    this._gridConfiguration.matcher = matcher;
    return this;
  }

  public withEnumType(enumType: BmEnumType) {
    this._gridConfiguration.enumType = enumType;
    return this;
  }

  public withEnumFilter(enumFilter: (item: BaseEnumType) => boolean) {
    this._gridConfiguration.enumFilter = enumFilter;
    return this;
  }

  public allowGrouping(allow: boolean = true) {
    this._gridConfiguration.groupable = allow;
    return this;
  }

  public setRemovable(removable: boolean) {
    this._gridConfiguration.removable = removable;
    return this;
  }

  public allowSortingAndFiltering(allow: boolean = true) {
    this._gridConfiguration.sortableAndFilterable = allow;
    return this;
  }

  public showInGridControls(showInGridControls: boolean = true) {
    this._gridConfiguration.showInGridControls = showInGridControls;
    return this;
  }

  public withSuffixExtractor(suffixExtractor: (item: T, value: any) => any) {
    this._gridConfiguration.rendererMetadata.suffixExtractor = suffixExtractor;
    return this;
  }

  public withPrefixExtractor(prefixExtractor: (item: T, value: any) => any) {
    this._gridConfiguration.rendererMetadata.prefixExtractor = prefixExtractor;
    return this;
  }

  public withClassExtractor(classExtractor: (item: T) => string) {
    this._gridConfiguration.classExtractor = classExtractor;
    return this;
  }

  public withFormatter(formatter: (value: any, item: T | undefined) => any) {
    this._gridConfiguration.formatter = formatter;
    return this;
  }

  public withIconSuffix(iconSuffixExtractor: (item: T, value: any) => any) {
    this._gridConfiguration.rendererMetadata.iconSuffixExtractor =
      iconSuffixExtractor;
    return this;
  }

  public withCsvFormatter(
    formatter: (
      value: any,
      item: T | undefined,
      stringFormatter: (s: string) => string
    ) => string
  ) {
    this._gridConfiguration.csvformatter = formatter;
    return this;
  }

  /**
   * Allows for router-links inside grids, does not work inconjunction with @row-click behaviours, or non-text fields.
   *
   * @param locationExtractor To return a router route definition to redirect to
   * @param openInNewTab Open link in a new tab
   */
  public withRoutingLink(
    locationExtractor: (item: T, value: any) => Location | null,
    opts: { openInNewTab?: boolean } = { openInNewTab: true }
  ) {
    this._gridConfiguration.rendererMetadata.locationExtractor =
      locationExtractor;
    this._gridConfiguration.rendererMetadata.newTab = opts.openInNewTab;
    return this;
  }

  public withExternalLink(
    externalLinkExtractor: (item: T, value: any) => string | null,
    openInNewTab: boolean = true
  ) {
    this._gridConfiguration.rendererMetadata.externalLinkExtractor =
      externalLinkExtractor;
    this._gridConfiguration.rendererMetadata.newTab = openInNewTab;
    return this;
  }

  public withEmailLink(itemExtractor: (item: T, value: any) => string | null) {
    this._gridConfiguration.rendererMetadata.emailExtractor = itemExtractor;
    return this;
  }

  public withPhoneLink(itemExtractor: (item: T, value: any) => string | null) {
    this._gridConfiguration.rendererMetadata.phoneExtractor = itemExtractor;
    return this;
  }

  public defaultSort(sortRankIndex: number = 0, isDecending = false) {
    this._defaultState.sortByIndex = sortRankIndex;
    this._defaultState.sortByDescending = isDecending;
    return this;
  }

  public defaultHide(hide: boolean = true) {
    this._defaultState.hidden = true;
    return this;
  }

  public defaultGroupBy(groupRankIndex: number = 0) {
    this._defaultState.groupByIndex = groupRankIndex;
    return this;
  }

  public defaultApplyFilter(filter: any) {
    this._defaultState.filterState = filter;
    return this;
  }

  public defaultApplyShowBooleanFalseFilter() {
    this._defaultState.filterState = {
      filter1: {
        type: "filter_equals",
        value: false,
      },
      // We need to provide both filters, otherwise it seems to break
      filter2: {
        type: "filter_show_all",
        value: "",
        operator: "and",
      },
    };
    return this;
  }

  public showNarrow(narrow = true) {
    this._gridConfiguration.shrink = narrow;
    return this;
  }

  /**
   * Add editing capabilities (via popup dialog) to the values within a column.
   *
   * @param renderer The renderer that will render the cell and provide an editing GUI.
   * @param editInserter Function to insert the modified data back into the row item.
   * @param editControlProps Object of properties to assign to the edit control. For example you can add validation
   * rules via the 'rules' property or set any property that is suitable to the underlying control.
   * @param editDialogTitle A custom title to add to the dialog. Defaults to the column heading text.
   * @param editItemValue A custom extractor for the edit control. Defaults to the column extractor (with no formatter).
   * @param dialogWidth A custom width for the dialog in pixels. Defaults to 200px.
   * @param rowEditable A custom extractor to determine if a row is editable or not.
   */
  public withEditDialog(
    renderer: Renderer | VueConstructor<Vue>,
    editInserter: (item: T, value: any) => void,
    editControlProps: object | ((item: T) => object) = {},
    editDialogTitle?: string,
    editItemValue?: (item: T) => any,
    dialogWidth: number = 200,
    rowEditable?: (item: T) => boolean
  ) {
    this._gridConfiguration.renderer = isVueComponent(renderer)
      ? componentToRenderer(renderer)
      : renderer;
    this._gridConfiguration.rendererMetadata.editRenderer = {
      editDialogTitle:
        editDialogTitle !== undefined
          ? editDialogTitle
          : this._gridConfiguration.headingText,
      editItemValue: editItemValue || this._gridConfiguration.extractor,
      editInserter,
      editControlProps,
      dialogWidth,
      rowEditable: rowEditable || (() => true),
    } as EditableRendererMetadata;
    return this;
  }

  /**
   * Truncates text if it is over a given length and allows for a dialog to replace
   * Defaults to length 30
   * @TODO: Parameters are not used for AG grid. Update when BM Grid is updated.
   *
   * @param options Customize how the text truncations behaves
   */

  public withTextTruncation(options?: Partial<TextTruncationOptions<T>>) {
    this._gridConfiguration.renderer = componentToRenderer(DataCellTextInfo);

    this._gridConfiguration.rendererMetadata.textTruncationOptions = {
      length: options?.length ?? 30,
      title: options?.title ?? this._gridConfiguration.headingText,
      props: options?.props,
      width: options?.width ?? 800,
      separator: options?.separator ?? " ",
      omission: options?.omission ?? "...",
      extractor: this._gridConfiguration.extractor,
    } as TextTruncationOptions<T>;

    return this;
  }

  /** Adds a per-column footer
   * NB This is only used in table-view, as a per-column footer does not make sense for cards.
   * Use the Summary section for a footer under the card view
   * @param renderer
   */
  public withFooter(renderer: ColumnFooterRenderer) {
    this._gridConfiguration.footer = renderer;
    return this;
  }

  public withColumnFilter(columnFilter: ColumnFilter) {
    this._gridConfiguration.columnFilter = columnFilter;
    return this;
  }
}

export class ActionColumnBuilder<T> extends ColumnBuilder<T, void> {
  constructor(columnKey: ColumnKey) {
    super(columnKey, () => undefined);
    this.allowSortingAndFiltering(false).withRenderer(
      componentToRenderer(DataCellActions)
    );
  }

  public withAction(
    translation: string | ((item: T) => string),
    onClick: (item: T) => void,
    isActive: (item: T) => boolean = (): boolean => true
  ) {
    this._gridConfiguration.rendererMetadata.actions =
      this._gridConfiguration.rendererMetadata.actions || [];

    this._gridConfiguration.rendererMetadata.actions.push({
      translation:
        translation instanceof Function ? translation : () => translation,
      onClick,
      isActive,
    });

    return this;
  }

  public withHideUnhide(
    itemIsHidden: (item: T) => boolean,
    performToggle: (item: T) => void,
    isActive: (item: T) => boolean = (): boolean => true
  ) {
    return this.withAction(
      (item) => (itemIsHidden(item) ? messages.unhide : messages.hide),
      performToggle,
      isActive
    );
  }

  /**
   * @deprecated Use withAction instead, passing text not key.
   */
  public withActionByKey(
    translationKeyOrFn: Key | ((item: T) => Key),
    onClick: (item: T) => void,
    isActive: (item: T) => boolean = (): boolean => true
  ) {
    this._gridConfiguration.rendererMetadata.actions =
      this._gridConfiguration.rendererMetadata.actions || [];

    this._gridConfiguration.rendererMetadata.actions.push({
      translation:
        translationKeyOrFn instanceof Function
          ? (item: T) => translate(translationKeyOrFn(item))
          : () => translate(translationKeyOrFn),
      onClick,
      isActive,
    });

    return this;
  }

  public withRoutingAction(
    translation: string | ((item: T) => string),
    route: ((item: T) => Location) | Location,
    isActive: (item: T) => boolean = (): boolean => true
  ) {
    this._gridConfiguration.rendererMetadata.actions =
      this._gridConfiguration.rendererMetadata.actions || [];

    this._gridConfiguration.rendererMetadata.actions.push({
      translation:
        translation instanceof Function ? translation : () => translation,
      route,
      isActive,
    });

    return this;
  }

  /**
   * @deprecated Use withRoutingAction instead, passing text not key.
   */
  public withRoutingActionByKey(
    translationKeyOrFn: Key | ((item: T) => Key),
    route: ((item: T) => Location) | Location,
    isActive: (item: T) => boolean = (): boolean => true
  ) {
    this._gridConfiguration.rendererMetadata.actions =
      this._gridConfiguration.rendererMetadata.actions || [];

    this._gridConfiguration.rendererMetadata.actions.push({
      translation:
        translationKeyOrFn instanceof Function
          ? (item: T) => translate(translationKeyOrFn(item))
          : () => translate(translationKeyOrFn),
      route,
      isActive,
    });

    return this;
  }

  public withActions(actionBuilder: (item: T) => RowAction[]) {
    this._gridConfiguration.rendererMetadata.actions =
      this._gridConfiguration.rendererMetadata.actions || [];

    this._gridConfiguration.rendererMetadata.actions.push(actionBuilder);
    return this;
  }
}

export class NumberColumnBuilder<T> extends ColumnBuilder<T, number | null> {
  constructor(
    columnKey: ColumnKey,
    itemValue: (item: T) => number | null,
    integral: boolean,
    maxDecimalPlaces?: number
  ) {
    super(columnKey, itemValue);

    this.withFormatter((itemValue: number | null) => {
      if (itemValue == null) {
        return "-";
      }

      if (integral) {
        return itemValue.toFixed(0);
      } else {
        return tenantService.formatNumber(
          itemValue,
          undefined,
          maxDecimalPlaces
        );
      }
    });

    this.withCsvFormatter((value: number | null) => {
      if (value == null) {
        return "";
      }
      if (integral) {
        return value.toFixed(0);
      } else {
        return value.toString(10);
      }
    });

    this.withColumnFilter({
      columnFilterComponent: NumberFilterComponent,
      buildApply: buildApplyNumberFilter,
    });
  }
}

export class ControlColumnBuilder<T> extends ColumnBuilder<T, void> {
  constructor(
    columnKey: ColumnKey,
    itemValue: (item: T) => any = () => undefined
  ) {
    super(columnKey, itemValue);
    this.withRenderer(componentToRenderer(DataCellControl));
  }

  /**
   * Define the control to render in the column.
   *
   * @param component The component constructor.
   * @param propBinder function that configures the properties to bind. https://vuejs.org/v2/api/#v-bind
   * @param eventBinder function that configures the events to bind. Look at object syntax for v-on: https://vuejs.org/v2/api/#v-on
   * @param contentBinder function that provides the content to render as a child of the control.
   */
  withControl(
    component: VueConstructor | ComponentPublicInstanceConstructor,
    propBinder?: (item: any) => any,
    eventBinder?: (item: any) => any,
    contentBinder?: (item: any) => any
  ) {
    this._gridConfiguration.rendererMetadata.control = {
      component,
      propBinder,
      eventBinder,
      contentBinder,
    };
    return this;
  }
}
